import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import { FormBorder, FormContents, FormInput } from 'components/stateless/CommonForm';
import GuideMessage, { MessageType } from 'components/stateless/GuideMessage/GuideMessage';
import ReasonModal from 'components/stateless/Modal/common/ReasonModal';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { AUTHORITY_TYPE, COMMON_APPROVAL_TYPE } from 'enums';
import useMounted from 'hooks/useMounted';
import getStatusBadgeClass from 'utils/classNames/getStatusBadgeClass';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { getSignIn } from 'utils/storage/LocalStorage';

import useAnchorDealerCompanyInfoWaitingDetailPageState from './useAnchorDealerCompanyInfoWaitingDetailPageState';
import RelatedAnchorAgreementList from '../../components/RelatedAnchorAgreementList';

function AnchorDealerCompanyInfoWaitingDetailPage() {
  const { t } = useTranslation();
  const currentAuthority = getSignIn()?.authorityType;
  const mounted = useMounted();
  const modal = useModal();

  const [editable, setEditable] = useState<boolean>(false);

  const isApproveAuthority =
    currentAuthority === AUTHORITY_TYPE.AUTHORIZER || currentAuthority === AUTHORITY_TYPE.ADMIN;
  const isCancelAuthority =
    currentAuthority === AUTHORITY_TYPE.HQ_OPERATOR || currentAuthority === AUTHORITY_TYPE.OPERATOR;

  const {
    fetchAll,
    formMethods,
    paginate,
    pageable,
    relatedAnchorAgreement,
    waitingAnchorDealerInfo,
    cancelRequest,
    checkOtpAuth,
    revertRequest,
    requestChange,
  } = useAnchorDealerCompanyInfoWaitingDetailPageState();

  const { register } = formMethods;

  useEffect(() => {
    if (mounted) {
      fetchAll();
    }
  }, [mounted]);

  useEffect(() => {
    setEditable(
      isCancelAuthority && waitingAnchorDealerInfo?.approvalType === COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED,
    );
  }, [waitingAnchorDealerInfo]);

  const handleClickCancel = () => {
    modal.show(
      <h6>
        {t('text:Would_you_like_to_cancel_the_registration')}{' '}
        {t('text:The_information_will_not_be_saved_if_registration_is_cancelled')}
      </h6>,
      {
        modalSize: ModalSize.NONE,
        modalType: ModalType.CONFIRM,
        closeBtnText: t('text:Close'),
        confirmBtnText: t('text:Confirm'),
        confirmBtnCb: () => cancelRequest(),
      },
    );
  };

  const handelClickRevert = () => {
    let reason = '';
    modal.show(
      <>
        <h6>
          {t('text:Would_you_like_to_revert_the_registration_request?')}
          <br />
          {t('text:Please_type_below_the_reason_for_the_revert')}
          <br />
          {t('text:Click_on_Confirm_to_revert_the_request')}
        </h6>
        <ReasonModal
          modalId={modal.id}
          getReturnReason={(returnReason: string) => {
            reason = returnReason;
          }}
        />
      </>,
      {
        modalSize: ModalSize.NONE,
        modalType: ModalType.CONFIRM,
        closeBtnText: t('text:Cancel'),
        confirmBtnText: t('text:Confirm'),
        confirmBtnCb: () => revertRequest(reason),
      },
    );
  };

  const handleClickRequestChange = async () => {
    modal.show(<h6>{t('text:Would_you_like_to_modify_the_dealers_information?')}</h6>, {
      modalType: ModalType.CONFIRM,
      confirmBtnCb: async () => {
        const isSuccess = await requestChange();
        if (isSuccess) {
          modal.show(
            <h6>
              {t('text:Request_for_modification_approval_has_been_completed')}
              <br />
              {t('text:Modification_will_be_completed_after_approval')}
            </h6>,
            {
              closeBtnCb: () => {
                fetchAll();
              },
            },
          );
        }
      },
    });
  };

  const handleClickConfirm = () => {
    modal.show(<h6>{t('text:Would_you_like_to_confirm_the_Uploaded_Dealer_modification_request?')}</h6>, {
      modalSize: ModalSize.NONE,
      modalType: ModalType.CONFIRM,
      closeBtnText: t('text:Close'),
      confirmBtnText: t('text:Confirm'),
      confirmBtnCb: () => checkOtpAuth(),
    });
  };

  const StatusBadgeComponent = () => {
    const getStatusBadgeUserInfo = (): JSX.Element => {
      switch (waitingAnchorDealerInfo?.approvalType) {
        case COMMON_APPROVAL_TYPE.OPERATOR_REQUEST:
        case COMMON_APPROVAL_TYPE.OPERATOR_CANCEL:
          return (
            <>
              <span className="supply-chain-partner-requester-info-title">
                {t('text:Admin_Operator')} ({waitingAnchorDealerInfo.operatorUserName} /{' '}
                {waitingAnchorDealerInfo?.operatorUserLoginId})
              </span>
              <span>{waitingAnchorDealerInfo?.operatorEnterpriseName}</span>
            </>
          );
        case COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED:
          return (
            <>
              <span className="supply-chain-partner-requester-info-title">
                {t('text:Admin')} ({waitingAnchorDealerInfo.authorizerUserName} /{' '}
                {waitingAnchorDealerInfo?.authorizerUserLoginId})
              </span>
              <span>{waitingAnchorDealerInfo?.authorizerEnterpriseName}</span>
            </>
          );
        default:
          return <></>;
      }
    };

    const getStatusTimeText = (): string => {
      switch (waitingAnchorDealerInfo?.approvalType) {
        case COMMON_APPROVAL_TYPE.OPERATOR_REQUEST:
          return t('text:Requested_Date');
        case COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED:
          return t('text:Reverted_Date');
        case COMMON_APPROVAL_TYPE.OPERATOR_CANCEL:
          return t('text:Cancelled_Date');
        default:
          return '';
      }
    };

    return (
      <div className="content-area">
        <div className="supply-chain-partner-status-block">
          <span
            className={`me-4 ${getStatusBadgeClass('COMMON_APPROVAL_TYPE', waitingAnchorDealerInfo?.approvalType)}`}
          >
            {t(`code:common-approval-type.${waitingAnchorDealerInfo?.approvalType}`)}
          </span>
          <span className="supply-chain-partner-requester-info">{getStatusBadgeUserInfo()}</span>
          <span className="supply-chain-partner-requester-info supply-chain-partner-requester-info-time">
            <span className="supply-chain-partner-requester-info-title">{getStatusTimeText()}</span>
            <span>{t('format:datetime', { value: waitingAnchorDealerInfo?.updatedDateTime, key: 'datetime' })}</span>
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <BackHeaderTitle title={waitingAnchorDealerInfo?.name} />
      {isApproveAuthority && waitingAnchorDealerInfo?.approvalType === COMMON_APPROVAL_TYPE.OPERATOR_REQUEST && (
        <GuideMessage
          message={[
            t('text:The_registration_request_for_the_Dealer_below_has_been_submitted'),
            t('text:Click_on_the_Confirm_button_to_approve_the_registration_request'),
            t(
              'text:If_the_company_s_tax_code_is_incorrect_click_on_the_Revert_button_and_return_the_registration_request',
            ),
          ]}
        />
      )}
      {waitingAnchorDealerInfo?.approvalType === COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED && (
        <GuideMessage
          message={[t('text:The_Dealer_registration_request_has_been_reverted')]}
          messageType={MessageType.ALERT}
          reasonTitle={t('text:REASON_FOR_REVERT')}
          reason={waitingAnchorDealerInfo?.rejectReason}
        />
      )}
      <StatusBadgeComponent />
      <div className="content-area">
        <SectionTitle title={t(`text:Dealer_Information`)} />
        <FormBorder>
          <FormContents>
            <div className="row">
              <FormInput
                label={t(`text:Dealer_Name`)}
                name="waitingAnchorDealer.name"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
              <FormInput
                label={t(`text:Tax_Code`)}
                name="waitingAnchorDealer.taxCode"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
            </div>
            <div className="row">
              <FormInput
                label={t(`text:Company_Registration_Number`)}
                name="waitingAnchorDealer.businessCode"
                ref={register}
                disabled={!editable}
              />
              <FormInput
                col={3}
                label={t(`text:Telephone`)}
                name="waitingAnchorDealer.telephone"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
              <FormInput
                col={3}
                label={t(`text:Fax`)}
                name="waitingAnchorDealer.fax"
                ref={register}
                disabled={!editable}
              />
            </div>
            <div className="row">
              <FormInput
                label={t(`text:Legal_Representative_Name`)}
                name="waitingAnchorDealer.representativeName"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
              <FormInput
                col={3}
                label={t(`text:Legal_Representative_Title`)}
                name="waitingAnchorDealer.representativePosition"
                ref={register}
                disabled={!editable}
              />
              <FormInput
                col={3}
                label={t(`text:Legal_Representative_Email`)}
                name="waitingAnchorDealer.representativeEmail"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
            </div>
            <div className="row">
              <FormInput
                col={12}
                label={t(`text:Registered_Office_Address`)}
                name="waitingAnchorDealer.address"
                ref={register}
                requiredOptions={{ required: true }}
                disabled={!editable}
              />
            </div>
          </FormContents>
        </FormBorder>
      </div>
      <RelatedAnchorAgreementList
        relatedAnchorAgreement={relatedAnchorAgreement}
        pageable={pageable}
        paginate={paginate}
      />
      {waitingAnchorDealerInfo?.approvalType === COMMON_APPROVAL_TYPE.OPERATOR_REQUEST && (
        <div className="content-area flex-end mt-3">
          {isCancelAuthority && (
            <Button
              size={ButtonSizeEnum.LG}
              onClick={handleClickCancel}
              variant={ButtonVariantEnum.OUTLINED}
              color={ButtonColorEnum.SECONDARY}
            >
              {t('text:Cancel_Request')}
            </Button>
          )}
          {isApproveAuthority && (
            <>
              <Button
                size={ButtonSizeEnum.LG}
                onClick={handelClickRevert}
                variant={ButtonVariantEnum.OUTLINED}
                color={ButtonColorEnum.RED}
                className="me-2"
              >
                {t('text:Revert')}
              </Button>
              <Button size={ButtonSizeEnum.LG} onClick={handleClickConfirm}>
                {t('text:Confirm')}
              </Button>
            </>
          )}
        </div>
      )}
      {isCancelAuthority && waitingAnchorDealerInfo?.approvalType === COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED && (
        <div className="content-area flex-end mt-3">
          <Button
            size={ButtonSizeEnum.LG}
            onClick={handleClickCancel}
            variant={ButtonVariantEnum.OUTLINED}
            color={ButtonColorEnum.SECONDARY}
            className="me-2"
          >
            {t('text:Cancel_Request')}
          </Button>
          <Button size={ButtonSizeEnum.LG} onClick={handleClickRequestChange}>
            {t('text:Request_Change')}
          </Button>
        </div>
      )}
    </>
  );
}

export default AnchorDealerCompanyInfoWaitingDetailPage;
